import { useState } from "react";
import { DownloadOutlined, SwapOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Dropdown, message } from "antd";
import hasPermission from "../PermissionHocs/hasPermission";
import { downloadExcel } from "../../utils/exportExcel";
import { neutral, typography } from "../../theme/tokens";

/**
 * Nhóm "Nhập / Xuất" chuẩn của mọi màn danh sách CMS — nằm trong slot `actions` của
 * `ListToolbar`, cạnh nút "Thêm mới".
 *
 * Trước đây mỗi màn tự bày một kiểu: Khách hàng gộp dropdown, Nhân sự để hai nút rời,
 * Sản phẩm/Quà tặng/Nhật ký chỉ có nút "Xuất Excel", vài màn còn tự gọi axios kèm token
 * rồi tự tạo thẻ <a> để tải. Nay mọi màn cùng một nút, cùng nhãn, cùng vị trí.
 *
 * Màn CHƯA có luồng nhập vẫn hiện mục "Tải lên danh sách" ở trạng thái mờ kèm chú
 * thích — người dùng biết chỗ này về sau sẽ nhập được, không phải đi tìm.
 *
 * Quyền: thiếu quyền thì ẨN mục đó (giống bản Khách hàng đang chạy). Không còn mục nào
 * hiện được thì không render nút.
 *
 * @param {func}   onImport      Mở màn nhập. Không truyền = màn chưa hỗ trợ nhập.
 * @param {array}  importActions Quyền cần có để thấy mục nhập.
 * @param {string} exportUrl     Endpoint xuất (đi qua `downloadExcel`, có sẵn token).
 * @param {object} exportParams  Query params — thường là bộ lọc đang xem.
 * @param {func}   onExport      Dùng thay `exportUrl` cho màn có luồng xuất đặc thù.
 * @param {bool}   exporting     Cờ loading khi tự xử lý xuất bằng `onExport`.
 */
export default function ImportExportMenu({
  onImport,
  importActions = [],
  importLabel = "Tải lên danh sách",
  importHint = "Màn này chưa hỗ trợ nhập từ tệp Excel",

  exportUrl,
  exportParams,
  exportFileName = "export.xlsx",
  exportMethod = "get",
  exportData,
  exportActions = [],
  exportLabel = "Xuất báo cáo",
  exportSuccessMessage = "Xuất Excel thành công",
  onExport,
  exporting,

  label = "Nhập / Xuất",
  disabled = false,
  ...rest
}) {
  const [downloading, setDownloading] = useState(false);

  const canImport = hasPermission({ actions: importActions });
  const canExport = (exportUrl || onExport) && hasPermission({ actions: exportActions });

  const handleExport = async () => {
    if (onExport) {
      onExport();
      return;
    }

    if (downloading) return;
    setDownloading(true);
    try {
      await downloadExcel(exportUrl, {
        params: exportParams,
        fileName: exportFileName,
        method: exportMethod,
        data: exportData,
      });
      if (exportSuccessMessage) message.success(exportSuccessMessage);
    } catch (error) {
      message.error(error?.message || "Không thể xuất file. Vui lòng thử lại!");
    } finally {
      setDownloading(false);
    }
  };

  const items = [];

  if (canImport) {
    items.push({
      key: "import",
      icon: <UploadOutlined />,
      disabled: !onImport,
      label: onImport ? (
        importLabel
      ) : (
        <span>
          {importLabel}
          <div style={{ fontSize: typography.size.sm, color: neutral[500] }}>{importHint}</div>
        </span>
      ),
      onClick: onImport,
    });
  }

  if (canExport) {
    items.push({
      key: "export",
      icon: <DownloadOutlined />,
      label: exportLabel,
      onClick: handleExport,
    });
  }

  if (items.length === 0) return null;

  return (
    <Dropdown menu={{ items }} trigger={["click"]} disabled={disabled}>
      <Button icon={<SwapOutlined />} loading={exporting ?? downloading} {...rest}>
        {label}
      </Button>
    </Dropdown>
  );
}
